import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Pagination, PAGE_SIZE } from "@/components/Pagination";
import { toast } from "sonner";
import { HoldBadge } from "@/components/HoldBadge";
import {
  Plus,
  ArrowsClockwise,
  ArrowsMerge,
  FolderSimple,
  Rows,
  SquaresFour,
  ChatCircleDots,
  CalendarBlank,
  Warning,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { AddressCombo } from "@/components/AddressCombo";
import { FilterBar } from "@/components/FilterBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DateField } from "@/components/DateTimeField";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DuplicateProjectsDialog } from "@/components/DuplicateProjectsDialog";

const STAGES = [
  "Lead",
  "Quoted",
  "Approved",
  "Template",
  "Fabrication",
  "Install",
  "Complete",
  "Lost",
];

const emptyProject = {
  name: "",
  customer_id: "",
  job_address: "",
  stage: "Lead",
  target_template_date: "",
  target_install_date: "",
  notes: "",
};

const STAGE_TONE = {
  Lead: "bg-zinc-500/15 text-zinc-300",
  Quoted: "bg-blue-500/15 text-blue-300",
  Approved: "bg-indigo-500/15 text-indigo-300",
  Template: "bg-amber-500/15 text-amber-300",
  Fabrication: "bg-orange-500/15 text-orange-300",
  Install: "bg-cyan-500/15 text-cyan-300",
  Complete: "bg-emerald-500/15 text-emerald-300",
  Lost: "bg-red-500/15 text-red-300",
};

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const day = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "—";

export const StageChip = ({ stage }) => (
  <span
    className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
      STAGE_TONE[stage] || STAGE_TONE.Lead
    }`}
  >
    {stage}
  </span>
);

// Soonest install for a project (job dates first, else target); null when done/none.
export const projectInstall = (p) => {
  if (["Complete", "Lost"].includes(p.stage)) return null;
  const date =
    (p.jobs || [])
      .map((j) => j.install_date)
      .filter(Boolean)
      .sort()[0] ||
    p.target_install_date ||
    "";
  if (!date) return null;
  const today = new Date().toISOString().slice(0, 10);
  const days = Math.round((Date.parse(date) - Date.parse(today)) / 86400000);
  return { date, days, overdue: days < 0, soon: days >= 0 && days <= 7 };
};

// Countdown to the soonest install; flags slipping jobs (overdue / due within a week).
export const InstallBadge = ({ p, className = "" }) => {
  const info = projectInstall(p);
  if (!info) return null;
  const { days, overdue, soon } = info;
  const tone = overdue
    ? "border-red-500/30 bg-red-500/15 text-red-300"
    : soon
      ? "border-amber-500/30 bg-amber-500/15 text-amber-300"
      : "border-zinc-700 bg-zinc-500/10 text-zinc-400";
  const label = overdue
    ? `Install ${Math.abs(days)}d late`
    : days === 0
      ? "Install today"
      : `Install in ${days}d`;
  return (
    <span
      data-testid={`project-install-badge-${p.id}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tone} ${className}`}
    >
      {(overdue || soon) && <Warning size={10} weight="fill" />}
      {label}
    </span>
  );
};

const QuoteChip = ({ quote, navigate, testid }) => {
  if (!quote?.id) return null;
  return (
    <span
      role="link"
      tabIndex={0}
      data-testid={testid}
      title="Open quote pricing"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/quotes/${quote.id}?step=3`);
      }}
      className="font-mono-data inline-flex cursor-pointer items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-semibold text-indigo-300 transition-colors duration-150 hover:bg-indigo-500/20"
    >
      {quote.code}
    </span>
  );
};

const QuoteChips = ({ quotes, navigate, testid }) => {
  const list = (quotes || []).filter((q) => q?.id);
  if (!list.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {list.map((q) => (
        <QuoteChip key={q.id} quote={q} navigate={navigate} testid={`${testid}-${q.code}`} />
      ))}
    </span>
  );
};

const normKey = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const dupKey = (p) => `${p.customer_id || ""}|${normKey(p.name)}|${normKey(p.job_address)}`;

const MergeMenu = ({ p, dups, onMerge, testid }) => {
  if (!dups.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={testid}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-500/20"
      >
        Merge
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
          Merge {p.code} into…
        </div>
        {dups.map((d) => (
          <DropdownMenuItem
            key={d.id}
            data-testid={`${testid}-into-${d.id}`}
            onClick={(e) => {
              e.preventDefault();
              onMerge(p.id, d.id);
            }}
          >
            {d.code} · {d.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function Projects({ embedded = false }) {
  const Wrap = embedded ? "div" : "main";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [stages, setStages] = useState([]);
  const [stage, setStage] = useState([]);
  const [q, setQ] = useState("");
  const [risk, setRisk] = useState(params.get("risk") === "risk" ? "risk" : "all");
  const [pay, setPay] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(null);
  const [dupsOpen, setDupsOpen] = useState(false);
  const [dupCount, setDupCount] = useState(0);
  const loadDupCount = useCallback(() => {
    api
      .get("/projects/duplicates")
      .then(({ data }) => setDupCount(data.groups.length))
      .catch(() => {});
  }, []);
  useEffect(() => {
    loadDupCount();
  }, [loadDupCount]);
  const [accounts, setAccounts] = useState([]);
  const [layout, setLayout] = useState(
    () => localStorage.getItem("bg_projects_view") || "table",
  );

  useEffect(() => {
    localStorage.setItem("bg_projects_view", layout);
  }, [layout]);

  const load = useCallback(async () => {
    const { data } = await api.get("/projects", {
      params: { q: q || undefined, archived: showArchived || undefined },
    });
    setRows(data.projects);
    setStages(data.stages);
  }, [q, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/customers")
      .then(({ data }) => setAccounts(data))
      .catch(() => {});
  }, []);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects/sync");
      toast.success(
        data.linked
          ? `${data.linked} quote${data.linked > 1 ? "s" : ""}/job${data.linked > 1 ? "s" : ""} grouped into projects`
          : "Everything is already grouped",
      );
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not sync",
      );
    }
    setBusy(false);
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects", {
        name: adding.name.trim(),
        customer_id: adding.customer_id,
        job_address: adding.job_address,
        stage: adding.stage || "Lead",
        target_template_date: adding.target_template_date || "",
        target_install_date: adding.target_install_date || "",
        notes: adding.notes || "",
      });
      toast.success("Project created");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not create the project",
      );
    }
    setBusy(false);
  };

  const matchesRisk = (p) => {
    if (risk === "all") return true;
    const info = projectInstall(p);
    if (!info) return false;
    if (risk === "overdue") return info.overdue;
    if (risk === "soon") return info.soon;
    return info.overdue || info.soon;
  };
  const matchesPay = (p) => {
    if (pay === "all") return true;
    if (p.stage !== "Complete") return false;
    const paid = (p.invoiced_total || 0) > 0 && (p.balance_due || 0) <= 0.009;
    return pay === "paid" ? paid : (p.balance_due || 0) > 0.009;
  };
  const mergeProject = async (fromId, intoId) => {
    try {
      await api.post("/projects/merge", { from_id: fromId, into_id: intoId });
      toast.success("Projects merged");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not merge the projects",
      );
    }
  };

  const dupGroups = {};
  rows.forEach((p) => {
    (dupGroups[dupKey(p)] = dupGroups[dupKey(p)] || []).push(p);
  });
  const dupsOf = (p) => (dupGroups[dupKey(p)] || []).filter((x) => x.id !== p.id);

  const visibleRows = rows
    .filter((p) => stage.length === 0 || stage.includes(p.stage))
    .filter(matchesRisk)
    .filter(matchesPay)
    .sort((a, b) => {
      if (risk === "all") return 0;
      const ad = projectInstall(a)?.date || "9999-99-99";
      const bd = projectInstall(b)?.date || "9999-99-99";
      return ad < bd ? -1 : ad > bd ? 1 : 0;
    });

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = visibleRows.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  return (
    <div className={embedded ? "" : "grain min-h-screen"} data-testid={embedded ? "board-project-list" : undefined}>
      {!embedded && <AppHeader title="Projects" />}
      <Wrap className={embedded ? "relative z-10 w-full" : "relative z-10 pb-28 pt-6"}>
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="projects-header-card"
        >
          {!embedded && (
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-600">
                Workflow
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Projects
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Every quote, job, drawing and slab layout for one job site, in
                one place
              </p>
            </div>
          </div>
          )}

          <div
            className={`flex flex-wrap items-center gap-2 ${embedded ? "" : "mt-5 border-t border-zinc-800 pt-4"}`}
            data-testid="projects-actions"
          >
            <Button
              data-testid="new-project-button"
              onClick={() => setAdding({ ...emptyProject })}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> New project
            </Button>
            <Button
              data-testid="sync-projects-button"
              variant="outline"
              disabled={busy}
              onClick={sync}
              className="h-12 border-zinc-800 bg-zinc-950 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800"
            >
              <ArrowsClockwise size={15} weight="bold" className="mr-2" /> Sync
              from quotes
            </Button>
            <Button
              data-testid="find-duplicates-button"
              variant="outline"
              onClick={() => setDupsOpen(true)}
              className="relative h-12 border-zinc-800 bg-zinc-950 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800"
            >
              <ArrowsMerge size={15} weight="bold" className="mr-2" /> Find duplicates
              {dupCount > 0 && (
                <span
                  data-testid="duplicates-badge"
                  className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-400 px-1.5 text-[10px] font-black text-zinc-950 shadow-[0_0_0_3px_rgba(9,9,11,1)]"
                >
                  {dupCount}
                </span>
              )}
            </Button>
            <DuplicateProjectsDialog
              open={dupsOpen}
              onOpenChange={setDupsOpen}
              onChanged={() => {
                load();
                loadDupCount();
              }}
            />
            <div className="hidden overflow-hidden rounded-md border border-zinc-800 sm:flex">
              {[
                ["table", Rows],
                ["cards", SquaresFour],
              ].map(([key, Icon]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`projects-view-${key}`}
                  onClick={() => setLayout(key)}
                  title={`${key} view`}
                  className={`flex h-12 items-center gap-2 px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    layout === key
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  <Icon size={16} weight="bold" /> {key}
                </button>
              ))}
            </div>
          </div>

        </div>

        <Dialog
          open={!!adding}
          onOpenChange={(o) => !o && setAdding(null)}
        >
          <DialogContent
            className="max-w-2xl border-zinc-800 bg-zinc-950"
            data-testid="new-project-dialog"
          >
            <DialogHeader>
              <DialogTitle className="text-lg font-bold tracking-tight">
                New project
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                Start a project folder — set the account, job site, stage and
                target dates so the schedule and quotes line up from day one.
              </DialogDescription>
            </DialogHeader>
            {adding && (
              <div
                className="grid gap-4 py-1"
                data-testid="new-project-form"
              >
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Project name
                  </Label>
                  <Input
                    data-testid="new-project-name"
                    value={adding.name}
                    onChange={(e) =>
                      setAdding({ ...adding, name: e.target.value })
                    }
                    placeholder="Project name — e.g. Smith kitchen"
                    className="h-11 border-zinc-800 bg-zinc-950"
                    autoFocus
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Account
                    </Label>
                    <SearchSelect
                      testId="new-project-account"
                      value={adding.customer_id}
                      placeholder="Pick an account"
                      searchPlaceholder="Search accounts…"
                      emptyText="No account matches"
                      options={accounts.map((a) => ({
                        value: a.id,
                        label: a.company
                          ? `${a.name} · ${a.company}`
                          : a.name,
                      }))}
                      onChange={(v) =>
                        setAdding({ ...adding, customer_id: v })
                      }
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Stage
                    </Label>
                    <Select
                      value={adding.stage || "Lead"}
                      onValueChange={(v) => setAdding({ ...adding, stage: v })}
                    >
                      <SelectTrigger
                        data-testid="new-project-stage"
                        className="h-11 border-zinc-800 bg-zinc-950"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STAGES.map((s) => (
                          <SelectItem
                            key={s}
                            value={s}
                            data-testid={`new-project-stage-${s.toLowerCase()}`}
                          >
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Job address
                  </Label>
                  <AddressCombo
                    testId="new-project-address"
                    value={adding.job_address}
                    onChange={(v) => setAdding({ ...adding, job_address: v })}
                    options={(() => {
                      const a = accounts.find(
                        (x) => x.id === adding.customer_id,
                      );
                      return a ? [a.address, ...(a.job_addresses || [])] : [];
                    })()}
                    placeholder="Job address"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Target template date
                    </Label>
                    <DateField
                      testId="new-project-template-date"
                      value={adding.target_template_date}
                      onChange={(v) =>
                        setAdding({ ...adding, target_template_date: v })
                      }
                      placeholder="Pick a date"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Target install date
                    </Label>
                    <DateField
                      testId="new-project-install-date"
                      value={adding.target_install_date}
                      onChange={(v) =>
                        setAdding({ ...adding, target_install_date: v })
                      }
                      placeholder="Pick a date"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Notes
                  </Label>
                  <Textarea
                    data-testid="new-project-notes"
                    value={adding.notes}
                    onChange={(e) =>
                      setAdding({ ...adding, notes: e.target.value })
                    }
                    placeholder="Anything the crew should know — gate codes, contacts, special asks…"
                    className="min-h-[84px] border-zinc-800 bg-zinc-950"
                  />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                data-testid="new-project-cancel"
                variant="outline"
                onClick={() => setAdding(null)}
                className="h-11 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
              >
                Cancel
              </Button>
              <Button
                data-testid="new-project-save"
                disabled={busy || !adding?.name.trim()}
                onClick={create}
                className="h-11 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                Create project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <FilterBar
          className="mt-6"
          testidPrefix="projects"
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Search projects, accounts, addresses…",
            testid: "project-search-input",
          }}
          sections={[
            {
              key: "stage",
              label: "Stage",
              multi: true,
              values: stage,
              allValue: "All",
              onChange: setStage,
              options: ["All", ...stages].map((s) => [
                s,
                s,
                `project-stage-filter-${s.toLowerCase()}`,
              ]),
            },
            {
              key: "risk",
              label: "Install timing",
              value: risk,
              allValue: "all",
              sort: true,
              chipPrefix: "Install: ",
              onChange: setRisk,
              options: [
                ["all", "Any", "project-risk-filter-all"],
                ["risk", "At risk", "project-risk-filter-risk"],
                ["overdue", "Overdue", "project-risk-filter-overdue"],
                ["soon", "Due ≤7d", "project-risk-filter-soon"],
              ],
            },
            {
              key: "pay",
              label: "Payment",
              value: pay,
              allValue: "all",
              chipPrefix: "Payment: ",
              onChange: setPay,
              options: [
                ["all", "Any", "project-pay-filter-all"],
                ["paid", "Complete · paid", "project-pay-filter-paid"],
                ["unpaid", "Complete · balance due", "project-pay-filter-unpaid"],
              ],
            },
            {
              key: "archived",
              label: "Archive",
              value: showArchived ? "archived" : "active",
              allValue: "active",
              chipPrefix: "",
              onChange: (v) => setShowArchived(v === "archived"),
              options: [
                ["active", "Active projects", "project-archive-filter-active"],
                ["archived", "Archived", "project-archive-filter-archived"],
              ],
            },
          ]}
        />

        {visibleRows.length === 0 ? (
          <div
            className="mt-6 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            data-testid="projects-empty"
          >
            <FolderSimple
              size={28}
              weight="bold"
              className="mx-auto text-zinc-700"
            />
            <p className="mt-3 text-sm text-zinc-400">
              {rows.length === 0
                ? "No projects yet — press Sync from quotes to group what you already have."
                : "No projects match this filter."}
            </p>
          </div>
        ) : (
          <>
            {layout === "table" && (
              <div
                className="mt-6 hidden overflow-hidden rounded-lg border border-zinc-800 lg:block"
                data-testid="projects-table"
              >
                <div className="grid grid-cols-[1.8fr_1.4fr_0.9fr_0.7fr_0.7fr_0.9fr_1fr] gap-4 border-b border-zinc-800 bg-zinc-950 px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-500">
                  <span>Project</span>
                  <span>Account · site</span>
                  <span>Stage</span>
                  <span>Quotes</span>
                  <span>Jobs</span>
                  <span>Next date</span>
                  <span className="text-right">Value</span>
                </div>
                {paged.map((p) => (
                  <Link
                    key={p.id}
                    to={`/projects/${p.id}`}
                    data-testid={`project-row-${p.id}`}
                    className="grid grid-cols-[1.8fr_1.4fr_0.9fr_0.7fr_0.7fr_0.9fr_1fr] items-center gap-4 border-b border-zinc-800/70 bg-zinc-900 px-4 py-3 transition-colors duration-150 last:border-0 hover:bg-zinc-800/40"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold">
                        {p.name}
                      </span>
                      <span className="font-mono-data block text-[11px] text-zinc-500">
                        {p.code}
                      </span>
                    </span>
                    <span className="min-w-0 truncate text-xs text-zinc-400">
                      {[p.customer_name, p.job_address]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </span>
                    <span className="flex items-center gap-2">
                      <StageChip stage={p.stage} />
                      {p.unread_notes > 0 && (
                        <span
                          className="flex items-center gap-1 text-[10px] font-bold text-indigo-300"
                          data-testid={`project-row-unread-${p.id}`}
                        >
                          <ChatCircleDots size={11} weight="bold" />
                          {p.unread_notes}
                        </span>
                      )}
                    </span>
                    <span className="flex flex-col items-start gap-1">
                      <span className="flex items-center gap-2">
                        <span className="font-mono-data text-sm text-zinc-300">
                          {p.quotes.length || "—"}
                        </span>
                        <MergeMenu
                          p={p}
                          dups={dupsOf(p)}
                          onMerge={mergeProject}
                          testid={`project-row-${p.id}-merge`}
                        />
                      </span>
                      <QuoteChips
                        quotes={p.quotes}
                        navigate={navigate}
                        testid={`project-row-quote-chip-${p.id}`}
                      />
                    </span>
                    <span className="font-mono-data text-sm text-zinc-300">
                      {p.jobs.length || "—"}
                    </span>
                    <span className="flex flex-col items-start gap-1 text-xs text-zinc-400">
                      <span>{day(p.next_date)}</span>
                      <InstallBadge p={p} />
                      <HoldBadge status={p.hold_status} since={p.hold_since} note={p.hold_note} testId={`project-hold-${p.id}`} />
                    </span>
                    <span className="font-mono-data text-right text-sm font-bold">
                      {money(p.accepted_total || p.quoted_total)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
            <div
              className={`mt-6 grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2 xl:grid-cols-3 ${
                layout === "table" ? "lg:hidden" : ""
              }`}
              data-testid="projects-list"
            >
              {paged.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  data-testid={`project-card-${p.id}`}
                  className="group min-w-0 max-w-full overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-500/60 sm:p-5"
                >
                  <div className="flex items-center gap-2 sm:hidden">
                    <span className="font-mono-data shrink-0 text-[11px] text-zinc-500">{p.code}</span>
                    <span className="min-w-0 flex-1 truncate text-sm font-bold">{p.name}</span>
                    <span className="font-mono-data shrink-0 text-sm font-bold">
                      {money(p.accepted_total || p.quoted_total)}
                    </span>
                  </div>
                  <div className="hidden sm:block">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono-data text-[11px] text-zinc-500">
                      {p.code}
                    </span>
                    <StageChip stage={p.stage} />
                    <QuoteChips
                      quotes={p.quotes}
                      navigate={navigate}
                      testid={`project-card-quote-chip-${p.id}`}
                    />
                    <MergeMenu
                      p={p}
                      dups={dupsOf(p)}
                      onMerge={mergeProject}
                      testid={`project-card-${p.id}-merge`}
                    />
                    {p.unread_notes > 0 && (
                      <span
                        className="flex items-center gap-1 rounded-full bg-indigo-500/15 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-indigo-300"
                        data-testid={`project-unread-${p.id}`}
                      >
                        <ChatCircleDots size={11} weight="bold" />{" "}
                        {p.unread_notes}
                      </span>
                    )}
                    <span className="font-mono-data ml-auto text-base font-bold">
                      {money(p.accepted_total || p.quoted_total)}
                    </span>
                  </div>
                  <h3 className="mt-2 truncate text-base font-bold group-hover:text-indigo-200">
                    {p.name}
                  </h3>
                  <p className="truncate text-xs text-zinc-500">
                    {[p.customer_name, p.job_address]
                      .filter(Boolean)
                      .join(" · ") || "No account linked"}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-zinc-800 pt-3 text-[11px] uppercase tracking-widest text-zinc-500">
                    <span>
                      <span className="font-mono-data text-zinc-300">
                        {p.quotes.length}
                      </span>{" "}
                      quotes
                    </span>
                    <span>
                      <span className="font-mono-data text-zinc-300">
                        {p.jobs.length}
                      </span>{" "}
                      jobs
                    </span>
                    {p.sqft > 0 && (
                      <span>
                        <span className="font-mono-data text-zinc-300">
                          {p.sqft}
                        </span>{" "}
                        ft²
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-1.5">
                      <CalendarBlank size={12} weight="bold" />
                      {day(p.next_date)}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <InstallBadge p={p} />
                    <HoldBadge status={p.hold_status} since={p.hold_since} note={p.hold_note} testId={`project-card-hold-${p.id}`} />
                  </div>
                  </div>
                </Link>
              ))}
            </div>
            <Pagination
              page={curPage}
              total={visibleRows.length}
              onPage={setPage}
              testId="projects-pagination"
            />
          </>
        )}
      </Wrap>
    </div>
  );
}
